import {
  FT_AUTOCOMPLETE,
  FT_LIST,
  FT_RANGE,
  FT_SINGLE,
  FT_SUGGESTION,
  FT_DATE,
  FT_CHECKBOX,
  FT_CONDITION_GROUP,
} from '../FilterTypes';
import {
  CAT_PRODUCTS,
  SUB_PRODUCTS_VAPING_DEV_ID,
  SUB_PRODUCTS_TEARDOWN_REPORTS,
  SUB_PRODUCTS_DHW_DEV_ID,
  SUB_PRODUCTS_WNS_DEV_ID,
} from 'App/utils/Collections';
import { FEAT_ID_FILLING_MATERIAL, FEAT_ID_HEATER_MATERIAL, FEAT_ID_INGREDIENTS } from 'Lib/enums/HardcodedIds';
import { ROLE_ADMIN, ROLE_CUSTOMER, ROLE_STAFF } from '@/app/constants/Roles';
import { STRATEGY_OR, STRATEGY_NOT } from './Strategy';
import {
  loadFeatures,
  loadProductStatuses,
  suggestCompany,
  searchBrand,
  loadFeatureListValues,
  loadProductFileGroups,
  searchUSPTypes,
  loadUSPTypes,
  loadLocations,
  productTypesLoader,
  searchTypes,
  searchConsumableVariations,
  searchCompetitorsTypes,
  loadCompetitors,
  economicRegions,
  loadCannaCompetitors,
  searchCannaCompetitors,
  loadIngredientCategories,
  loadBigTobaccoTypes,
  searchBigTobaccoTypes,
  loadMajorPlayersTypes,
  searchMajorPlayersTypes,
  searchIngredientCategories,
} from './loaders';
import { searchStaff } from '@/components/filters/config/loaders/users';
import { i18n } from '@/app/plugins/i18n';

const SPLITTABLE_HINT = 'You can type multiple values separated by comma, semicolon, or space.';

export const groups = {
  business: {
    name: 'Business',
    groupOrderIndex: 0,
  },
  date: {
    name: 'Date',
    groupOrderIndex: 1,
  },
  files: {
    name: 'Files',
    groupOrderIndex: 2,
  },
  general: {
    name: 'General',
    groupOrderIndex: 3,
  },
  innovations: {
    name: 'Innovations',
    groupOrderIndex: 4,
  },
  competitors: {
    name: 'Competitors',
    groupOrderIndex: 5,
  },
  features: {
    name: 'Features',
    groupOrderIndex: 99,
    isFeature: true,
    sm: 12,
    md: 12,
    lg: 12,
    xLg: 12,
  },
  ingredientsConcentration: {
    name: 'Ingredients Concentration',
    groupOrderIndex: 100,
    isFeature: true,
    sm: 12,
    md: 12,
    lg: 12,
    xLg: 12,
  },
};

const itemsMapper = ({ id, name }) => {
  return { text: name, value: id };
};

const compare = (str, term) => {
  const rex = new RegExp(term, 'i');
  return rex.test(str);
};

export default {
  category: CAT_PRODUCTS,
  resources: [
    {
      name: 'features',
      loader: async () =>
        await loadFeatures(
          groups.features,
          {
            not_id: FEAT_ID_INGREDIENTS,
            applicableEntities: 'product',
            'and_group_or_parent[1]': [FEAT_ID_INGREDIENTS, 'not'],
            locale: i18n.locale,
          },
          { favoriteConditionGroup: true }
        ),
    },
    {
      name: 'ingredientsConcentration',
      loader: async () =>
        await loadFeatures(groups.ingredientsConcentration, {
          applicableEntities: 'product',
          'and_group_or_parent[1]': [FEAT_ID_INGREDIENTS],
          locale: i18n.locale,
        }),
    },
  ],
  filters: [
    {
      id: 'id',
      label: 'ID Number',
      type: FT_SINGLE,
      group: groups.general,
      splittable: true,
      hint: SPLITTABLE_HINT,
    },
    {
      id: 'name',
      label: 'Product Name',
      type: FT_SINGLE,
      group: groups.general,
      hint: '',
    },
    {
      id: 'and_group_or_types',
      propertyName: 'and_group_or_types',
      label: 'Product Type',
      group: groups.general,
      type: FT_CONDITION_GROUP,
      placeholder: 'Choose type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchTypes,
        items: productTypesLoader,
      },
      children: ['and_group_or_types__usp'],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'productionStatus.id',
      label: 'Production Status',
      group: groups.business,
      type: FT_LIST,
      getListItems: loadProductStatuses,
    },
    {
      id: 'card_status',
      label: 'Сard status',
      group: groups.innovations,
      type: FT_LIST,
      getListItems: async () => [
        { id: 'draft', name: 'Draft' },
        { id: 'published', name: 'Published' },
        { id: 'ai_ready', name: 'AI Ready' },
        { id: 'ai_processing', name: 'AI Processing' },
      ],
      overrideCollectValues: true,
      mutuallyExclusiveGroups: [
        ['draft', 'published'],
        ['ai_ready', 'ai_processing'],
      ],
      customCollectValues: function () {
        if (!this.value || this.value.length === 0) {
          return {};
        }

        const result = {};

        if (this.value.includes('draft')) {
          result.draft = true;
        } else if (this.value.includes('published')) {
          result.draft = false;
        }

        if (this.value.includes('ai_ready')) {
          result.aiReady = true;
        } else if (this.value.includes('ai_processing')) {
          result.aiReady = false;
        }

        return result;
      },
      roles: [ROLE_STAFF],
    },
    {
      id: 'manufacturers.name',
      label: 'Company/Manufacturer',
      group: groups.business,
      type: FT_SUGGESTION,
      getSuggestions: suggestCompany,
      useNASearch: true,
      naProp: 'exists[manufacturers]',
    },
    {
      id: 'brands',
      label: 'Brands',
      type: FT_AUTOCOMPLETE,
      group: groups.business,
      fieldConf: {
        searcher: searchBrand,
      },
      useNASearch: true,
      naProp: 'exists[brands]',
    },
    {
      id: 'and_group_or_marketCountries.country',
      label: 'Market Countries',
      group: groups.business,
      type: FT_CONDITION_GROUP,
      placeholder: 'Choose Market Country',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'countries',
        searcher: loadLocations,
        items: loadLocations,
      },
      useNASearch: true,
      naProp: 'exists[marketCountries]',
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'marketCountries.country.economicRegions.id',
      label: 'Economic region',
      type: FT_LIST,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      group: groups.business,
      getListItems: economicRegions,
    },
    {
      id: 'and_group_or_feature_group_option',
      label: 'Ingredient category',
      type: FT_CONDITION_GROUP,
      group: groups.general,
      placeholder: 'Choose category',
      fieldConf: {
        searcher: searchIngredientCategories,
        items: loadIngredientCategories,
      },
      categories: [SUB_PRODUCTS_WNS_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'filling_material',
      label: 'Filling Material',
      group: groups.general,
      type: FT_CONDITION_GROUP,
      placeholder: 'Choose material',
      fieldConf: {
        isFavorite: true,
        searcher: async (term) => {
          return (await loadFeatureListValues(FEAT_ID_FILLING_MATERIAL)).then((res) => {
            return res.filter(({ name }) => compare(name, term)).map(itemsMapper);
          });
        },
        items: async () => {
          return loadFeatureListValues(FEAT_ID_FILLING_MATERIAL).then((res) => res.map(itemsMapper));
        },
      },
      isFeature: true,
      featureID: FEAT_ID_FILLING_MATERIAL,
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_TEARDOWN_REPORTS],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: `feature${FEAT_ID_HEATER_MATERIAL}`,
      label: 'Heater Material',
      type: FT_LIST,
      group: groups.general,
      featureID: FEAT_ID_HEATER_MATERIAL,
      isFeature: true,
      useStrategies: true,
      strategy: STRATEGY_OR,
      useNASearch: true,
      hide: true,
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_TEARDOWN_REPORTS],
      getListItems: async () => loadFeatureListValues(FEAT_ID_HEATER_MATERIAL),
    },
    {
      id: `feature${FEAT_ID_INGREDIENTS}_subcategory`,
      label: 'Ingredients',
      group: groups.general,
      type: FT_CONDITION_GROUP,
      placeholder: 'Choose ingredient',
      fieldConf: {
        isFavorite: true,
        searcher: () => {},
        items: async (conf = {}) => {
          return loadFeatureListValues(FEAT_ID_INGREDIENTS, conf).then((res) => res.map(itemsMapper));
        },
      },
      isFeature: true,
      featureID: FEAT_ID_INGREDIENTS,
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_WNS_DEV_ID, SUB_PRODUCTS_TEARDOWN_REPORTS],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: `not_feature${FEAT_ID_FILLING_MATERIAL}`,
      label: 'NOT Filling Material',
      type: FT_LIST,
      group: groups.general,
      featureID: FEAT_ID_FILLING_MATERIAL,
      isFeature: true,
      strategy: STRATEGY_NOT,
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_TEARDOWN_REPORTS],
      getListItems: async () => loadFeatureListValues(FEAT_ID_FILLING_MATERIAL),
      hide: false,
    },
    // not dhw
    {
      id: 'variations.variation.id',
      label: 'Consumable Variations',
      group: groups.general,
      type: FT_AUTOCOMPLETE,
      fieldConf: {
        searcher: searchConsumableVariations,
      },
      roles: [ROLE_STAFF, ROLE_ADMIN],
      useNASearch: true,
      naProp: 'exists[variations]',
      categories: [SUB_PRODUCTS_VAPING_DEV_ID],
    },
    {
      id: 'priceRange',
      label: 'Retail price',
      type: FT_RANGE,
      group: groups.business,
      isLteGte: true,
      hint: 'per 1 unit',
    },
    {
      id: 'appeared',
      label: 'First Mention',
      group: groups.date,
      type: FT_DATE,
      tooltipText: 'First public mention about the product',
    },
    {
      id: 'createdAt',
      label: 'Created',
      group: groups.date,
      type: FT_DATE,
    },
    {
      id: 'files.createdAt',
      label: 'Files Added',
      group: groups.date,
      type: FT_DATE,
      // hide: true,
    },
    {
      id: 'marketCountries.startedOn',
      label: 'Market Launch',
      group: groups.date,
      type: FT_DATE,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_DHW_DEV_ID],
    },
    {
      id: 'exists[files]',
      label: 'Contains Files',
      fieldConf: {
        label: 'Show items with files',
      },
      group: groups.files,
      type: FT_CHECKBOX,
      defaultValue: () => true,
    },
    {
      id: 'files.fileGroup',
      label: 'Groups',
      group: groups.files,
      type: FT_LIST,
      getListItems: loadProductFileGroups,
    },
    {
      id: 'exists[patents]',
      label: 'Patents',
      group: groups.files,
      fieldConf: {
        label: 'Show items with patents',
      },
      type: FT_CHECKBOX,
      defaultValue: () => true,
    },
    {
      id: 'company',
      label: 'Company ID Number',
      type: FT_LIST,
      group: groups.general,
      hide: true,
    },
    {
      id: 'manufacturers',
      label: 'Company ID Number (with Brands)',
      type: FT_SINGLE,
      group: groups.general,
      hide: true,
      hint: '',
    },
    {
      id: 'brands.tradingCompanies',
      label: 'Trading Company ID',
      type: FT_SINGLE,
      group: groups.general,
      hide: true,
      hint: '',
    },
    {
      id: 'componentsInProduct.component.id',
      label: 'Component ID Number',
      type: FT_SINGLE,
      group: groups.general,
      hide: true,
      hint: '',
    },
    {
      id: 'patents.id',
      label: 'Patents',
      group: groups.general,
      type: FT_LIST,
      hide: true,
    },
    // not dhw
    {
      id: 'exists[usp]',
      label: 'Products with USP',
      group: groups.innovations,
      roles: [ROLE_STAFF, ROLE_CUSTOMER],
      type: FT_CHECKBOX,
      defaultValue: () => true,
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'and_group_or_types__usp',
      propertyName: 'and_group_or_types',
      label: 'USP types',
      group: groups.innovations,
      type: FT_CONDITION_GROUP,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      placeholder: 'Choose USP type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchUSPTypes,
        items: loadUSPTypes,
      },
      parent: 'and_group_or_types',
      childOptions: {
        childItems: loadUSPTypes,
      },
      categories: [SUB_PRODUCTS_VAPING_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    // not dhw
    {
      id: 'and_group_or_types__comp',
      propertyName: 'and_group_or_types',
      label: 'BT & Major Players',
      group: groups.competitors,
      type: FT_CONDITION_GROUP,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      placeholder: 'Choose type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchCompetitorsTypes,
        items: loadCompetitors,
      },
      selectAllOnActivate: true,
      parent: 'and_group_or_types',
      childOptions: {
        childItems: loadCompetitors,
      },
      categories: [SUB_PRODUCTS_VAPING_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'and_group_or_types__bt',
      propertyName: 'and_group_or_types',
      label: 'Big Tobacco',
      group: groups.competitors,
      type: FT_CONDITION_GROUP,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      placeholder: 'Choose type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchBigTobaccoTypes,
        items: loadBigTobaccoTypes,
      },
      selectAllOnActivate: true,
      parent: 'and_group_or_types',
      childOptions: {
        childItems: loadBigTobaccoTypes,
      },
      categories: [SUB_PRODUCTS_WNS_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'and_group_or_types__mp',
      propertyName: 'and_group_or_types',
      label: 'Major Players',
      group: groups.competitors,
      type: FT_CONDITION_GROUP,
      roles: [ROLE_STAFF, ROLE_ADMIN, ROLE_CUSTOMER],
      placeholder: 'Choose type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchMajorPlayersTypes,
        items: loadMajorPlayersTypes,
      },
      selectAllOnActivate: true,
      parent: 'and_group_or_types',
      childOptions: {
        childItems: loadMajorPlayersTypes,
      },
      categories: [SUB_PRODUCTS_WNS_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'and_group_or_types__canna',
      propertyName: 'and_group_or_types',
      label: 'Cannabis',
      group: groups.competitors,
      type: FT_CONDITION_GROUP,
      placeholder: 'Choose type',
      fieldConf: {
        isFavorite: true,
        favoriteKind: 'types',
        searcher: searchCannaCompetitors,
        items: loadCannaCompetitors,
      },
      selectAllOnActivate: true,
      parent: 'and_group_or_types',
      childOptions: {
        childItems: loadCannaCompetitors,
      },
      categories: [SUB_PRODUCTS_VAPING_DEV_ID, SUB_PRODUCTS_WNS_DEV_ID],
      style: {
        'font-weight': 700,
      },
    },
    {
      id: 'marketCountries.upc',
      label: 'UPC',
      type: FT_SINGLE,
      group: groups.business,
      hint: SPLITTABLE_HINT,
      splittable: true,
      categories: [SUB_PRODUCTS_WNS_DEV_ID],
      useNASearch: true,
      naProp: 'exists[marketCountries.upc]',
      tooltipText: 'Universal Product Code',
      hide: true,
    },
    {
      id: 'created.id',
      label: 'Creator',
      group: groups.general,
      type: FT_AUTOCOMPLETE,
      fieldConf: {
        searcher: searchStaff,
      },
      roles: [ROLE_STAFF, ROLE_ADMIN],
    },
    {
      id: 'barcode',
      label: 'EAN/UPC',
      type: FT_SINGLE,
      group: groups.business,
      hint: 'The barcode must be between 6 and 13 digits',
      categories: [SUB_PRODUCTS_WNS_DEV_ID, SUB_PRODUCTS_VAPING_DEV_ID],
      tooltipText: 'Product codes',
      hide: false,
    }, // UPC: 850040427295 EAN-13: 0850040427295
  ],
  searchBarProp: 'complex_name',
};
